'use client';

import { Icon } from '@iconify/react';
import { ProjectCard } from '../ui/project-card';
import Link from 'next/link';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { type Project as SanityProject } from '@/app/lib/data';
import { useTranslation } from 'react-i18next';

/**
 * Projects section component
 * Displays the recent projects from Sanity CMS
 * Fetches project data including title, description, icons, and tech tags
 * Responsive grid layout for different screen sizes
 * Enhanced with professional design and animations
 */
export function ProjectsSection({ projects }: { projects: SanityProject[] }) {
  const { t } = useTranslation();

  const sectionRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tl = gsap.timeline();

    if (sectionRef.current) gsap.set(sectionRef.current, { opacity: 0 });

    tl.to(sectionRef.current, { opacity: 1, duration: 0.6 });

    if (headerRef.current) {
      tl.fromTo(headerRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.5, ease: "back.out(1.5)" }, "-=0.2");
    }

    if (gridRef.current) {
      tl.fromTo(gridRef.current.children,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.1, ease: "power2.out" },
        "-=0.3"
      );
    }
  }, [projects]);

  // Enhanced skeleton loader for better visual representation during loading
  const ProjectSkeleton = () => (
    <div className="rounded-xl border border-slate-200/50 dark:border-slate-700/60 bg-white dark:bg-card h-full animate-pulse">
      <div className="p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700"></div>
          <div className="h-4 w-4 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
        </div>
        <div className="h-5 w-3/4 bg-slate-200 dark:bg-slate-700 rounded-md mb-3"></div>
        <div className="space-y-2">
          <div className="h-3 w-full bg-slate-200 dark:bg-slate-700 rounded-md"></div>
          <div className="h-3 w-5/6 bg-slate-200 dark:bg-slate-700 rounded-md"></div>
          <div className="h-3 w-4/6 bg-slate-200 dark:bg-slate-700 rounded-md"></div>
        </div>
        <div className="flex gap-1.5 mt-4">
          <div className="h-5 w-16 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
          <div className="h-5 w-12 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
          <div className="h-5 w-14 bg-slate-200 dark:bg-slate-700 rounded-full"></div>
        </div>
      </div>
    </div>
  );



  return (
    <section
      ref={sectionRef}
      className="mb-16 sm:mb-20"
    >
      <div
        ref={headerRef}
        className="flex justify-between items-center mb-6 sm:mb-10"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 shadow-sm">
            <Icon icon="carbon:portfolio" className="h-6 w-6 text-primary dark:text-primary/90" />
          </div>
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">{t('Projects.sectionTitle')}</h2>
            <p className="text-sm text-slate-700 dark:text-muted-foreground mt-0.5">{t('Projects.sectionSubtitle')}</p>
          </div>
        </div>
        <Link
          href="/projects"
          prefetch={false}
          className="flex items-center gap-2 text-sm font-medium text-primary hover:text-primary transition-colors group px-4 py-2 rounded-full border border-slate-200 dark:border-slate-700/60 hover:border-primary/20 dark:hover:border-primary/20 bg-white/50 dark:bg-card backdrop-blur-sm"
        >
          {t('Projects.viewAll')}
          <Icon icon="carbon:arrow-right" className="h-3.5 w-3.5 group-hover:translate-x-1 transition-transform" />
        </Link>
      </div>

      <div
        ref={gridRef}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 sm:gap-6"
      >
        {(!projects || projects.length === 0) ? (
          // Display skeleton loaders if no projects are provided
          Array.from({ length: 4 }).map((_, index) => (
            <div key={`skeleton-${index}`}>
              <ProjectSkeleton />
            </div>
          ))
        ) : (
          // Display actual projects
          projects.map((project) => (
            <div
              key={project._id}
            >
              <ProjectCard
                title={project.title}
                description={project.description}
                href={`/projects/${project.slug?.current || ''}`}
                icon={project.iconName || 'carbon:code'}
                imageSrc={project.mainImage}
                tags={(project.technologies ?? []).map(tech => tech.name)}
                translationKey={project.translationKey}
              />
            </div>
          ))
        )}
      </div>
    </section>
  );
} 